<template>
    <Modal class="mellon-webecr-transactions-modal"
           data-component="MellonWebECRTransactionsModal"
           title="MELLON_WEBECR.TRANSACTIONS_MODAL_TITLE"
           icon="fa-solid fa-clipboard-check"
           :cancel="cancel">
        <div class="alert alert-info">
            {{ l('MELLON_WEBECR.TRANSACTIONS_MODAL_INFO') }}
        </div>

        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <ErrorMessage v-else-if="error"
                      :error="error" />

        <template v-else>
            <div v-if="transactionIntents && transactionIntents.length === 0"
                 class="alert alert-info">
                {{ l('MELLON_WEBECR.TRANSACTIONS_MODAL_NONE') }}
            </div>
            <table v-else
                   class="table table-striped table-hover">
                <thead>
                    <tr>
                        <th>
                            {{ l('PAYMENT.CREATED_AT') }}
                            <small class="block">{{ l('PAYMENT.REFERENCE') }}</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.AMOUNT') }}
                            <small class="block">&nbsp;</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.STATUS') }}
                            <small class="block">{{ l('PAYMENT.AUTH_CODE') }}</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.CARD_NUMBER') }}
                            <small class="block">&nbsp;</small>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <TxnRow v-for="intent in transactionIntents"
                            :key="intent.Id"
                            :intent="intent"
                            :transaction="intent.Transaction ? transactions[intent.Transaction] : null"
                            :payment="intent.Transaction ? payments[transactions[intent.Transaction]?.CustomerReference] : null"
                            :voiding="voidingTransactionIntentID ? voidingTransactionIntentID === intent.Id : null"
                            @void="voidTransaction" />
                </tbody>
            </table>
        </template>
    </Modal>
</template>

<script>
import BigNumber from 'bignumber.js';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import MellonWebECRTransactionRow from '@/epos/vue/modals/Payment/PaymentMethods/MellonWebECR/MellonWebECRTransactionRow';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PaymentMethodRefEnum from '@/lib/PaymentMethods/PaymentMethodRef';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

/**
 * Displays a list of all MellonWebECR transactions that have been started for this terminal in the last 12 hours.
 * We actually display transaction *intents* for completeness. That means it will include declined and failed
 * transactions.
 *
 * Mellon say that transactions are voidable until the end of the day.
 */
export default {
    components: {
        TxnRow: MellonWebECRTransactionRow, // Alias used to make the template lines less long.
        ErrorMessage,
        PageLoadingSpinner,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            /**
             * @type {?Error}
             */
            error: null,

            /**
             * @type {boolean}
             */
            loading: false,

            /**
             * @type {Object<number, MellonWebECRTransaction>}
             */
            transactions: {},

            /**
             * @type {?MellonWebECRTransactionIntent[]}
             */
            transactionIntents: null,

            /**
             * @type {Object<string, object>}
             */
            payments: {},

            /**
             * @type {?number}
             */
            voidingTransactionIntentID: null,
        };
    },

    created() {
        this.loadTransactions();
    },

    methods: {
        async loadTransactions() {
            this.loading = true;

            const device = container.currentDevice.device;
            const config = device.getPaymentTerminalConfigForMethod(
                PaymentMethodRefEnum.CARD_MELLON_WEBECR,
            );

            // Load all transaction intents and transactions for the current terminal for the last 6 hours.

            // Both these endpoints return results in chronological order. That makes it not easy to just take the
            // last 100 transactions. We could only get the 100 next transactions after a given timestamp.
            // So we'll do it properly - load all transactions and intents since a given timestamp (6 hours ago) and
            // follow the pagination to get all results.
            // We request 500 results per page so it's unlikely there's a second page of either.

            let timestampMin = new Date();
            timestampMin.setHours(timestampMin.getHours() - 6);
            timestampMin = timestampMin.toISOString();

            const promises = [];

            promises.push(
                this.loadAllIntents(config, timestampMin)
                    .then((intents) => {
                        // The results are returned in chronological order.
                        // We want the most recent transactions first.
                        // And then after getting all that data, to not show too much data, we only show the last 100.
                        this.transactionIntents = intents.reverse().splice(0, 100);
                    })
            );

            promises.push(
                this.loadAllTransactions(config, timestampMin)
                    .then((transactions) => {
                        this.transactions = transactions;
                    })
            );

            await Promise.all(promises);

            // Now we've loaded the 100 latest intents, and all the latest transactions,
            // lets check for DT payments that match the customer references on the intents.
            // We can't void any of those because they are already saved on the server.
            try {
                const thirdPartyIDs = this.transactionIntents
                    .map((intent) => intent.CustomerReference)
                    .filter((thirdPartyID) => !!thirdPartyID);

                this.payments = await this.loadAllPayments(thirdPartyIDs);
            } catch (e) {
                this.error = e;
            }

            this.loading = false;
        },

        /**
         * @param {MellonWebECRDeviceConfig} config
         * @param {number} timestampMin
         *
         * @returns {Promise<MellonWebECRTransactionIntent[]>}
         */
        async loadAllIntents(config, timestampMin) {
            const allIntents = [];

            // Load all transaction intents since the given timestamp, following the pagination to get all results.
            let offset = 0;
            let limit = 500;
            const getNextPage = async () => {
                return container.mellonWebECRDriver.client
                    .getTransactionIntents({
                        TID: config.terminalTerminalID,
                        Timestamp_min: timestampMin,
                        limit,
                        offset,
                    })
                    .then(async (response) => {
                        allIntents.push(
                            ...response.results.filter((intent) => {
                                // Filter out intents that were intents to void instead of transactions.
                                // Without doing this we'd list both a transaction, and then a void intent for
                                // that same transaction.
                                return !intent.Void; // Void is true for void intents.
                            })
                        );

                        if (response.next) {
                            // The response says there's another page. We don't need to use their URL
                            // beacuse we know it'll be the same URL with a different offset.
                            offset += limit;
                            await getNextPage();
                        }
                    });
            };

            await getNextPage();

            return allIntents;
        },

        /**
         * @param {MellonWebECRDeviceConfig} config
         * @param {number} timestampMin
         *
         * @returns {Promise<Object<number, MellonWebECRTransaction>>}
         */
        async loadAllTransactions(config, timestampMin) {
            const allTransactions = {};

            // Load all transactions since the given timestamp, following the pagination to get all results.
            let offset = 0;
            let limit = 500;
            const getNextPage = async () => {
                return container.mellonWebECRDriver.client
                    .getTransactions({
                        TID: config.terminalTerminalID,
                        Timestamp_min: timestampMin,
                        limit,
                        offset,
                    })
                    .then(async (response) => {
                        // Unlike intents, we store the transactions in an object with the transaction ID as the key.
                        // So we can just build the object as we go.
                        response.results.forEach((transaction) => {
                            allTransactions[transaction.Id] = transaction;
                        });

                        if (response.next) {
                            // The response says there's another page. We don't need to use their URL
                            // beacuse we know it'll be the same URL with a different offset.
                            offset += limit;
                            await getNextPage();
                        }
                    });
            };

            await getNextPage();

            return allTransactions;
        },

        /**
         * Load all DigiTickets payments with a thirdPartyID matching any of the current transactions.
         *
         * @param {string[]} thirdPartyIDs
         *
         * @returns {Promise<Object<string, object>>}
         */
        async loadAllPayments(thirdPartyIDs) {
            if (!thirdPartyIDs.length) {
                return {};
            }

            return container.apiClient
                .request(
                    'GET',
                    'payments/by-third-party-id',
                    {
                        thirdPartyIDs,
                    },
                )
                .then(({ data }) => {
                    const payments = {};
                    data.forEach((payment) => {
                        payments[payment.thirdPartyID] = payment;
                    });
                    return payments;
                });
        },

        /**
         * @param {Object} values
         * @param {MellonWebECRTransactionIntent} values.intent
         * @param {MellonWebECRTransaction} values.transaction
         *
         * @returns {Promise}
         */
        async voidTransaction({ intent, transaction }) {
            const confirmed = await container.dialogService.confirm(
                '',
                {
                    customText: container.langService.getText(
                        'PAYMENT.CONFIRM_VOID',
                        {
                            amount: new BigNumber(intent.Amount).dividedBy(100).toNumber(), // Convert to major units.
                            lastDigits: transaction.CardPAN.slice(-4),
                        }
                    ),
                }
            );

            if (!confirmed) {
                return;
            }

            this.voidingTransactionIntentID = intent.Id;

            container.mellonWebECRDriver
                .voidTransaction(
                    intent.Terminal,
                    intent.Id,
                )
                .then(({ response }) => {
                    // The void response includes the updated transaction.
                    // Update the transaction in the list.
                    const transactionId = response.Transaction.Id;
                    this.transactions[transactionId] = response.Transaction;
                })
                .finally(() => {
                    this.voidingTransactionIntentID = null;
                });
        },
    },
};
</script>

<style lang="less">
.mellon-webecr-transactions-modal {
    &__status-cell {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        grid-gap: 5px;
    }
}
</style>
